import Vue from 'vue';
import { ActionContext, ActionTree, Module, MutationTree, GetterTree } from 'vuex';
import { CostCentersState, UpdateCostCenterPayload, CostCenterDriver, CostCenterQueryParams, CompanyCoDriver } from './cost-centers.model';
import { t } from '@/utils/translation';
import { Group, PatchGroupPayload } from '@pirates/not-morty-biz/dist/services/groups/Types';
import { driverManagementService } from '../driver-management/driver-management.service';
import { CompanyDriverStatus, Driver } from '../driver-management/driver-management.model';
import { GETTER_CURRENT_COMPANY_ID } from '../company/company.store';
import { costCentersService } from './cost-centers.service';
import { concatenateName } from '@/utils/strings';

class CostCenters implements Module<CostCentersState, any> {
  public state: CostCentersState = {
    costCenters: [],
    pagination: {
      page: 1,
      total: 0,
      itemsPerPage: 10,
    },
    lastVisitedCostCenter: null,
    costCenterDriverMap: {},
    drivers: {
      filteredDrivers: [],
      addedDrivers: [],
      currentCostCenterDrivers: [],
    },
  };

  public namespaced: boolean = true;
  public actions = {
    async getCostCenters(store: ActionContext<CostCentersState, any>, params: { page?: number; searchQuery?: string }) {
      try {
        const currentPage = params?.page || store?.state?.pagination.page;

        const queryParams: CostCenterQueryParams = {
          skip: (currentPage - 1) * store.state.pagination.itemsPerPage,
          limit: store.state.pagination.itemsPerPage,
          refCompanyId: store.rootState.companies.company.companyId,
        };

        if (params?.searchQuery) {
          queryParams.search = params.searchQuery;
        }

        const { data: costCenters, meta } = await costCentersService.getCostCenters(queryParams);
        store.commit('SET_COST_CENTERS', costCenters);
        store.commit('SET_AVAILABLE_TOTAL_COST_CENTERS', meta.total);
        store.commit('SET_PAGE', currentPage);
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
      }
    },

    async getCostCenterById(store: ActionContext<CostCentersState, any>, params: { id: string; forceUpdate?: boolean }) {
      try {
        const { id, forceUpdate = false } = params;
        let costCenter = store.getters.loadActiveCostCenterById(id);
        if (forceUpdate || !costCenter) {
          costCenter = await costCentersService.getCostCenterById(id);

          store.commit('SET_COST_CENTERS', [costCenter]);
        }
        store.commit('SET_LAST_VISITED_COST_CENTER', costCenter);
        store.commit('CLEAR_ADDED_DRIVERS');

        await store.dispatch('getCostCenterDrivers');
        return costCenter;
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
      }
    },

    async createCostCenter(store: ActionContext<CostCentersState, any>, name: string) {
      try {
        await costCentersService.createCostCenter({
          name,
          refCompanyId: store.rootGetters[GETTER_CURRENT_COMPANY_ID],
          companyUuid: store.rootState.companies.company.uuid,
        });
        store.commit('SET_PAGE', 1);
        await store.dispatch('getCostCenters');

        Vue.utils.$toast(
          t('cost_center_created_successfully', {
            name,
          }),
          'success',
        );
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
      }
    },

    async updateCostCenter(store: ActionContext<CostCentersState, any>, payload: UpdateCostCenterPayload) {
      try {
        await costCentersService.patchCostCenter(store.state.lastVisitedCostCenter!.href, payload);
        store.commit('UPDATE_COST_CENTER', payload);
        await store.dispatch('getCostCenterById', { id: store.state.lastVisitedCostCenter!.id, forceUpdate: true });
        Vue.utils.$toast(
          t('cost_center_edited_successfully', {
            name: payload.name,
          }),
          'success',
        );
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
      }
    },

    async deleteCostCenter(store: ActionContext<CostCentersState, any>, params: { href: string; name: string }) {
      try {
        const { href, name } = params;
        await costCentersService.deleteCostCenter(href);
        const groupIndex = store.state.costCenters.findIndex((grp) => grp.href == href);
        store.commit('REMOVE_COST_CENTER', groupIndex);

        Vue.utils.$toast(
          t('cost_center_deleted_successfully', {
            name,
          }),
          'success',
        );
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
      }
    },

    async getCostCenterDrivers(store: ActionContext<CostCentersState, any>) {
      try {
        const data = await costCentersService.getDrivers(store.state.lastVisitedCostCenter!.id);
        const drivers = data.map((i) => ({
          ...i,
          memberName: concatenateName(i.firstName, i.lastName),
        }));
        store.commit('SET_COST_CENTER_DRIVERS', drivers);
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
      }
    },

    async setDriverCostCenterMapping(store: ActionContext<CostCentersState, any>) {
      try {
        const costCenterDetails = store.state.costCenters.map((costCenter) => {
          return {
            id: costCenter.id,
            name: costCenter.name,
          };
        });
        let costCenterDriverMap = {};

        const { data: drivers } = await driverManagementService.getAll({
          refCompanyId: store.rootGetters[GETTER_CURRENT_COMPANY_ID],
          status: CompanyDriverStatus.INVITATION_ACCEPTED,
        });

        drivers.forEach((driver) => {
          costCenterDriverMap[driver.customerId] = []; // Required to make section in add drivers modal reactive
        });

        for (let i = 0; i < costCenterDetails.length; i++) {
          const id = costCenterDetails[i].id;
          const name = costCenterDetails[i].name;
          const drivers = await costCentersService.getDrivers(id);

          drivers.forEach((driver) => {
            if (costCenterDriverMap[driver.customerId]) {
              costCenterDriverMap[driver.customerId].push(name);
            } else {
              costCenterDriverMap[driver.customerId] = [name];
            }
          });
        }

        store.commit('SET_COST_CENTER_DRIVER_MAP', costCenterDriverMap);
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
      }
    },

    async searchDrivers(store: ActionContext<CostCentersState, any>, search: string) {
      try {
        const { data } = await driverManagementService.getAll({
          refCompanyId: store.rootGetters[GETTER_CURRENT_COMPANY_ID],
          search,
          status: CompanyDriverStatus.INVITATION_ACCEPTED,
          limit: 20,
        });

        const drivers = data.map((i) => ({ ...i, memberName: concatenateName(i.firstName, i.lastName) }));
        store.commit('SET_FILTERED_DRIVERS', drivers);
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
      }
    },

    async getAllDrivers(store: ActionContext<CostCentersState, any>) {
      try {
        const { data } = await driverManagementService.getAll({
          refCompanyId: store.rootGetters[GETTER_CURRENT_COMPANY_ID],
          status: CompanyDriverStatus.INVITATION_ACCEPTED,
          limit: 20,
        });

        const drivers = data.map((i) => ({ ...i, memberName: concatenateName(i.firstName, i.lastName) }));
        store.commit('SET_FILTERED_DRIVERS', drivers);
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
      }
    },

    async addDriverToCostCenter(store: ActionContext<CostCentersState, any>, driver: Driver) {
      try {
        const coDriver: CompanyCoDriver = {
          customerId: driver.customerId,
          email: driver.email,
          customerUuid: driver.customerUuid,
          firstName: driver.firstName!,
          lastName: driver.lastName!,
          businessEmail: driver.businessEmail!,
        };

        const addedDriver = await costCentersService.addDriver(store.state.lastVisitedCostCenter!.id, coDriver);
        // add member name for list cell rendering
        addedDriver.memberName = concatenateName(addedDriver.firstName, addedDriver.lastName);
        store.commit('ADD_DRIVER_TO_COST_CENTER', addedDriver);
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
      }
    },

    async removeDriverFromCostCenter(store: ActionContext<CostCentersState, any>, toRemoveDriver: CostCenterDriver | Driver) {
      try {
        const customerId = toRemoveDriver.customerId;
        const addedDriverIndex = store.state.drivers.addedDrivers.findIndex((driver) => driver.customerId === customerId);
        const currentCostCenterDriverIndex = store.state.drivers.currentCostCenterDrivers.findIndex((driver) => driver.customerId === customerId);
        const driverHref = store.state.drivers.currentCostCenterDrivers[currentCostCenterDriverIndex].href;
        await costCentersService.removeDriver(driverHref);

        store.commit('REMOVE_COST_CENTER_DRIVER', { addedDriverIndex, currentCostCenterDriverIndex });
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
      }
    },
  } as ActionTree<CostCentersState, any>;

  public mutations = {
    SET_COST_CENTERS(state: CostCentersState, costCenters: Group[]) {
      state.costCenters = costCenters;
    },
    SET_AVAILABLE_TOTAL_COST_CENTERS(state: CostCentersState, total: number) {
      state.pagination.total = total;
    },
    UPDATE_COST_CENTER(state: CostCentersState, payload: PatchGroupPayload) {
      state.lastVisitedCostCenter!.name = payload.name;
    },
    REMOVE_COST_CENTER(state: CostCentersState, costCenterIndex: number) {
      state.costCenters.splice(costCenterIndex, 1);
    },
    SET_PAGE(state: CostCentersState, page) {
      state.pagination.page = page;
    },
    SET_LAST_VISITED_COST_CENTER(state: CostCentersState, costCenter) {
      state.lastVisitedCostCenter = costCenter;
    },
    SET_COST_CENTER_DRIVERS(state: CostCentersState, costCenterDrivers) {
      state.drivers.currentCostCenterDrivers = costCenterDrivers;
    },

    SET_FILTERED_DRIVERS(state: CostCentersState, filteredDrivers) {
      state.drivers.filteredDrivers = filteredDrivers;
    },
    SET_COST_CENTER_DRIVER_MAP(state: CostCentersState, costCenterDriverMap) {
      state.costCenterDriverMap = costCenterDriverMap;
    },
    CLEAR_ADDED_DRIVERS(state: CostCentersState) {
      state.drivers.addedDrivers = [];
    },
    ADD_DRIVER_TO_COST_CENTER(state: CostCentersState, driver: CostCenterDriver) {
      state.drivers.addedDrivers.push(driver);
      state.drivers.currentCostCenterDrivers.push(driver);
      if (state.costCenterDriverMap[driver.customerId]) {
        state.costCenterDriverMap[driver.customerId].push(state.lastVisitedCostCenter!.name);
      } else {
        state.costCenterDriverMap[driver.customerId] = [state.lastVisitedCostCenter!.name];
      }
    },
    REMOVE_COST_CENTER_DRIVER(state: CostCentersState, { addedDriverIndex, currentCostCenterDriverIndex }) {
      if (addedDriverIndex > -1) {
        state.drivers.addedDrivers.splice(addedDriverIndex, 1);
      }
      const driver = state.drivers.currentCostCenterDrivers.splice(currentCostCenterDriverIndex, 1)[0];
      state.costCenterDriverMap[driver.customerId] = state.costCenterDriverMap[driver.customerId].filter((costCenter) => costCenter !== state.lastVisitedCostCenter!.name);
    },
  } as MutationTree<CostCentersState>;

  public getters = {
    loadActiveCostCenterById: (state: CostCentersState) => (id: string) => {
      return state.costCenters.find((costCenter) => costCenter.id === id);
    },
    getDriversWithCostCenterNames: (state: CostCentersState) => () => {
      return state.drivers.filteredDrivers.map((driver) => {
        const costCenters = state.costCenterDriverMap[driver.customerId];
        return {
          ...driver,
          costCenters,
        };
      });
    },
    costCenterDrivers: (state: CostCentersState): CostCenterDriver[] => {
      return state.drivers.currentCostCenterDrivers.map((driver) => {
        return {
          ...driver,
          costCenters: state.costCenterDriverMap[driver.customerId],
        };
      });
    },
  } as GetterTree<any, any>;
}

export default new CostCenters();
